import { useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { GridRowParams } from '@material-ui/data-grid';
import { observer } from 'mobx-react-lite';
import _ from 'lodash';
import { toJS } from 'mobx';
import { companyDetailsParameters, companyDetailsRoute, route } from '../../utils/routing';
import { gridColumn } from '../../utils/gridUtils';
import TextLink from '../../components/other/TextLink';

import CompanyDetails from './CompanyDetails';
import { drawerStore } from '../../state/drawerStore';
import { FormInputTypes } from '../../utils/constants';
import { Api } from '../../api';
import { PAGES } from '../../components/main/MainComponent';
import { usePaginatedProvider } from '../../state/paginatedProviderStore';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';
import { ReactComponent as PrivateCompanyIcon } from '../../images/Locations/privateCompany.svg';
import { ReactComponent as PublicCompanyIcon } from '../../images/Locations/publicCompany.svg';
import { ReactComponent as AccountBalanceWallet } from '../../images/Companies/account_balance_wallet.svg';
import { ReactComponent as CompanyChargePoint } from '../../images/Companies/company_chargepoint.svg';
import { ReactComponent as CompanyLocation } from '../../images/Companies/company_location.svg';

const accountCreateInputs = [
  {
    name: 'type',
    label: 'Account type',
    type: FormInputTypes.select,
    options: ['private', 'company'],
  },
  { name: 'name', type: FormInputTypes.textField },
  { name: 'description', type: FormInputTypes.textField },
  { name: 'searchAddress', type: FormInputTypes.googleAddressAutocomplete },
  { name: 'address', type: FormInputTypes.textField },
  { name: 'zip', type: FormInputTypes.textField },
  { name: 'city', type: FormInputTypes.textField },
  { name: 'country', type: FormInputTypes.textField },
  { name: 'phone', type: FormInputTypes.textField },
  { name: 'email', type: FormInputTypes.textField },
];

const CompaniesPage = observer(() => {
  const companiesProvider = usePaginatedProvider(undefined, undefined, false);
  const [sortableFields, setSortableFields] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [walletCounts, setWalletCounts] = useState([]);

  useEffect(() => {
    companiesProvider.setRequest((offset, sortBy, sortOrder) =>
      Api.business.listAccounts({ skip: offset, take: 20, sortBy, sortOrder }),
    );

    companiesProvider.setOnFulfilled((_data, _count, sortable) => {
      if (sortable) {
        setSortableFields(sortable);
      }
    });
    companiesProvider.reloadResource();
  }, []);

  useEffect(() => {
    const fetchIndicators = async () => {
      if (companiesProvider.data?.length) {
        const companyIds = companiesProvider.data.map((company) => company.id);
        const indicatorsResponse = (await Api.charge.listIndicatorsForCompanies(companyIds))?.data
          .data;
        const walletCountsResponse = (await Api.billing.getWalletCountForAccounts(companyIds))?.data
          .data;
        setIndicators(_.keyBy(indicatorsResponse, 'accountId') || []);
        setWalletCounts(_.keyBy(walletCountsResponse, 'accountId') || []);
      }
    };
    fetchIndicators();
  }, [companiesProvider.data]);

  const onClickConfirmAdd = () => {
    const data = toJS(drawerStore.values);

    Api.business.createAccount(data).then(companiesProvider.reloadResource);
  };

  const onClickAccountAdd = () => {
    drawerStore.openDrawer({
      label: 'Create account',
      inputs: accountCreateInputs,
      clickCallback: onClickConfirmAdd,
    });
  };

  const chargePointCountsOfIndicators = (chargePoints) => {
    let count = 0;
    chargePoints?.forEach((item) => {
      if (Array.isArray(item)) {
        item?.forEach((subItem) => {
          count += subItem.length;
        });
      } else {
        count++;
      }
    });

    return count;
  };

  const companyColumns = [
    gridColumn('', {
      flex: 0.5,
      sortableFields,
      minWidth: 60,
      renderCell: (params: GridRowParams) => {
        const companyType = params.row.type;
        if (!companyType) {
          return null;
        }
        let IconComponent;
        switch (companyType) {
          case 'private':
            IconComponent = PrivateCompanyIcon;
            break;
          case 'company':
            IconComponent = PublicCompanyIcon;
            break;
          default:
            IconComponent = null;
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
            {IconComponent && <IconComponent style={{ marginBottom: '4px' }} />}
          </div>
        );
      },
    }),
    gridColumn('name', {
      flex: 4,
      sortableFields,
      minWidth: 150,
      renderCell: (params: GridRowParams) => (
        <TextLink label={params.row.name} to={companyDetailsParameters(params.row.id)} />
      ),
    }),
    gridColumn('address', {
      flex: 4,
      sortableFields,
      minWidth: 200,
      renderCell: (params: GridRowParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
          <div style={{ marginBottom: '0.375rem' }}>{params.row.address}</div>
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              fontSize: '0.9rem',
              color: 'gray',
              alignItems: 'center',
            }}
          >
            <span>{params.row.zip}</span>
            <span>{params.row.city}</span>
          </div>
        </div>
      ),
    }),
    gridColumn('indicators', {
      flex: 4,
      sortableFields,
      minWidth: 200,
      renderCell: (params: GridRowParams) => {
        const { locationCount = 0, chargePointCount = 0 } = indicators[params.row.id] ?? {};
        const { walletCount = 0 } = walletCounts[params.row.id] ?? {};

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', lineHeight: 'normal' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AccountBalanceWallet style={{ fontSize: '1.5rem' }} />
              <span>{walletCount}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CompanyChargePoint style={{ fontSize: '1.5rem' }} />
              <span>{chargePointCount}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CompanyLocation style={{ fontSize: '1.5rem' }} />
              <span>{locationCount}</span>
            </div>
          </div>
        );
      },
    }),
  ];

  return (
    <div style={{ width: '100%' }}>
      <VMPaginatedGrid
        onClickAdd={onClickAccountAdd}
        label="Companies"
        count={companiesProvider.count}
        rows={companiesProvider.data}
        columns={companyColumns}
        onPageOffsetChange={companiesProvider.onPageChange}
        onSortTypeChange={companiesProvider.setSort}
        rowHeight={60}
      />
    </div>
  );
});

const CompaniesPageWrapper = () => (
  <Switch>
    <Route exact path={route(PAGES.Companies)} component={CompaniesPage} />
    <Route path={companyDetailsRoute()} component={CompanyDetails} />
  </Switch>
);

export default CompaniesPageWrapper;
